import { Sparkles } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { cn } from '@/lib/utils'

/** The ring-and-tint of an AI proposal (as `MeetingReviewRow`, the journey ReviewRow D5 pattern). */
const SUGGESTED_CLASS = 'border-primary/60 bg-primary/5 ring-1 ring-primary/30'

/**
 * One proposal of Sparky with its tick, the key fields (children), the flags in text (IA-20)
 * and [Bewerken]. A waiting card (a choice under "Wie" is open) cannot be ticked and says why.
 */
export function ProposalCard({
  label,
  checked,
  onCheckedChange,
  waitingText,
  flags,
  error,
  onEdit,
  children,
}: {
  /** The accessible name of the tick: what the card is about. */
  label: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
  /** Set when the card cannot be ticked yet: the reason, shown in text. */
  waitingText?: string | undefined
  flags: string[]
  error?: string | undefined
  onEdit?: (() => void) | undefined
  children: ReactNode
}) {
  const waiting = waitingText !== undefined
  return (
    <div className={cn('rounded-2xl border p-3', checked ? SUGGESTED_CLASS : 'border-border bg-card')}>
      <div className="flex items-start gap-1">
        <label className="grid size-11 shrink-0 cursor-pointer place-items-center">
          <Checkbox
            aria-label={label}
            checked={checked}
            disabled={waiting}
            onChange={(event) => onCheckedChange(event.target.checked)}
          />
        </label>
        <div className="min-w-0 flex-1 space-y-1.5 pt-2">
          <p className="inline-flex items-center gap-1 text-xs font-semibold text-primary">
            <Sparkles aria-hidden className="size-3.5" />
            Voorstel van Sparky
          </p>
          {children}
          {waiting ? <p className="text-sm text-muted-foreground">{waitingText}</p> : null}
          {flags.map((flag) => (
            <p key={flag} className="text-sm text-muted-foreground">
              {flag}
            </p>
          ))}
          {error ? (
            <p role="alert" className="text-sm font-medium text-destructive">
              {error}
            </p>
          ) : null}
          {onEdit ? (
            <div className="pt-1">
              <Button type="button" variant="outline" size="sm" className="max-md:min-h-11" onClick={onEdit}>
                Bewerken
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}
